import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Copy, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Bandeau promo (haut du site, au-dessus de la navigation).
 *
 * Priorité :
 *  1) Si l'admin a activé une bannière via /api/promo-banner → utilise ses valeurs.
 *  2) Sinon, campagne par défaut "SOLDES DE LA RENTRÉE" (code 2625, -25%).
 *
 * Design : fond amber/orange doux et joyeux (jaune moutarde clair) — attire sans agresser.
 * Copie du code en un clic (data-testid promo-banner-copy).
 * Fermable et mémorisé par localStorage (réapparaît si la campagne change).
 */
const DEFAULT_BANNER = {
  active: false,   // Campagne rentrée terminée — bannière désactivée par défaut
  code: "2625",
  percent: 25,
  emoji: "🍎",
  label: "SOLDES DE LA RENTRÉE",
  cta: "Profiter maintenant",
  expires_at: null,
};

export default function PromoBanner() {
  const [banner, setBanner] = useState(DEFAULT_BANNER);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Try admin-configured banner first; fallback to default (rentrée)
    api.get("/promo-banner")
      .then((r) => {
        if (r.data?.active) {
          const b = {
            ...DEFAULT_BANNER,
            ...r.data,
            emoji: r.data.emoji || DEFAULT_BANNER.emoji,
            label: r.data.label || DEFAULT_BANNER.label,
          };
          setBanner(b);
          setDismissed(localStorage.getItem(`promo_banner_${b.code}`) === "1");
        } else {
          // Keep default rentrée
          setDismissed(localStorage.getItem(`promo_banner_${DEFAULT_BANNER.code}`) === "1");
        }
      })
      .catch(() => {
        setDismissed(localStorage.getItem(`promo_banner_${DEFAULT_BANNER.code}`) === "1");
      });
  }, []);

  if (!banner || !banner.active || dismissed) return null;

  const expiry = banner.expires_at
    ? ` · jusqu'au ${new Date(banner.expires_at).toLocaleDateString("fr-FR")}`
    : "";

  async function copyCode(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(banner.code);
      setCopied(true);
      toast.success(`Code « ${banner.code} » copié !`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier — sélectionnez le code manuellement");
    }
  }

  function dismiss() {
    localStorage.setItem(`promo_banner_${banner.code}`, "1");
    setDismissed(true);
  }

  return (
    <div
      className="relative bg-gradient-to-r from-amber-400 via-amber-300 to-orange-300 text-ink border-b border-amber-500/40"
      data-testid="promo-banner"
      role="region"
      aria-label="Bandeau promotionnel"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-2 flex items-center justify-center gap-2 sm:gap-3 pr-8 sm:pr-10">
        <Link
          to="/catalogue"
          className="flex items-center justify-center gap-1.5 sm:gap-2.5 text-center hover:opacity-90 transition min-w-0"
          data-testid="promo-banner-link"
        >
          <span className="text-base sm:text-lg shrink-0" aria-hidden>{banner.emoji}</span>
          <span className="text-[11px] sm:text-sm font-medium leading-tight">
            <span className="hidden sm:inline font-semibold tracking-wide">{banner.label} : </span>
            <span className="sm:hidden font-semibold">Rentrée · </span>
            <span className="font-bold">−{banner.percent}%</span>
            <span className="hidden sm:inline"> sur tout le site avec le code</span>
            <span className="sm:hidden"> avec code</span>
          </span>
        </Link>
        <button
          onClick={copyCode}
          data-testid="promo-banner-copy"
          className="inline-flex items-center gap-1 rounded-full bg-ink text-ivory text-[11px] sm:text-xs px-2.5 sm:px-3 py-1 hover:bg-ink/85 transition-all shrink-0 font-mono font-bold tracking-widest"
          title="Copier le code"
        >
          <span>{banner.code}</span>
          {copied ? <Check size={12} className="text-sage" /> : <Copy size={12} className="opacity-70" />}
        </button>
        {expiry && (
          <span className="hidden md:inline text-[11px] text-ink/70 italic">{expiry}</span>
        )}
      </div>
      <button
        onClick={dismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-ink/10 transition"
        aria-label="Fermer le bandeau"
        data-testid="promo-banner-close"
      >
        <X size={14} />
      </button>
    </div>
  );
}
